// ============================================================
// LinuxPackageManagement.jsx
//
// Branded, responsive course content page for the
// "Linux Package Management" lesson.
//
// Brand palette (consistent across all lesson pages):
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (bright gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl breakpoints)
//   - Heavily commented for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box, Container, Typography, Grid, Chip, Divider,
    Tooltip, Collapse, Paper,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import InventoryIcon           from "@mui/icons-material/Inventory";
import LayersIcon              from "@mui/icons-material/Layers";
import BuildIcon               from "@mui/icons-material/Build";
import SearchIcon              from "@mui/icons-material/Search";
import SecurityIcon            from "@mui/icons-material/Security";
import SettingsIcon            from "@mui/icons-material/Settings";
import StorageIcon             from "@mui/icons-material/Storage";
import CloudDownloadIcon       from "@mui/icons-material/CloudDownload";
import PublicIcon              from "@mui/icons-material/Public";
import ExtensionIcon           from "@mui/icons-material/Extension";
import TerminalIcon            from "@mui/icons-material/Terminal";
import AutorenewIcon           from "@mui/icons-material/Autorenew";
import DeleteIcon              from "@mui/icons-material/Delete";
import LinkIcon                from "@mui/icons-material/Link";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import ExpandLessIcon          from "@mui/icons-material/ExpandLess";
import ContentCopyIcon         from "@mui/icons-material/ContentCopy";
import DoneIcon                from "@mui/icons-material/Done";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon         from "@mui/icons-material/CheckCircle";
import LightbulbIcon           from "@mui/icons-material/Lightbulb";
import StarIcon                from "@mui/icons-material/Star";
import SchoolIcon              from "@mui/icons-material/School";
import WarningAmberIcon        from "@mui/icons-material/WarningAmber";
import LockIcon                from "@mui/icons-material/Lock";
import AccountTreeIcon         from "@mui/icons-material/AccountTree";
import SpeedIcon               from "@mui/icons-material/Speed";

// ── Brand tokens ──────────────────────────────────────────
// Centralise all colours — change here to retheme everything.
const BRAND = {
    bg:            "#233D4D",
    surface:       "rgba(255,255,255,0.04)",
    border:        "rgba(218,165,32,0.25)",
    borderHover:   "rgba(218,165,32,0.6)",
    gold:          "#DAA520",
    goldBright:    "#FFD700",
    goldGlow:      "rgba(218,165,32,0.15)",
    white:         "#FFFFFF",
    muted:         "rgba(255,255,255,0.55)",
    codeBg:        "rgba(0,0,0,0.45)",
    codeText:      "#FFD700",
    danger:        "rgba(255,80,80,0.12)",
    dangerBorder:  "rgba(255,80,80,0.35)",
    dangerText:    "#ff8080",
    success:       "rgba(39,201,63,0.10)",
    successBorder: "rgba(39,201,63,0.35)",
    successText:   "#27c93f",
    // Distro accent colours — each package manager gets its own identity
    apt:     "#e95420",   // Ubuntu orange
    dnf:     "#51a2da",   // Fedora blue
    pacman:  "#1793d1",   // Arch blue
    snap:    "#772953",   // Snap purple
    flatpak: "#4A90D9",   // Flatpak blue
    pip:     "#3572A5",   // Python blue
    npm:     "#CB3837",   // npm red
};

// ── Shared heading style ──────────────────────────────────
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ============================================================
//  SUB-COMPONENT: SectionTitle
//  Gold icon circle + heading — reused by every section.
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "50%", background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>{label}</Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
//  Hoverable card for one package management concept.
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper elevation={0} sx={{ p: 2.5, height: "100%", background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 3, transition: "border-color 0.25s, box-shadow 0.25s", "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 20px ${BRAND.goldGlow}` } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>{title}</Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>{description}</Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
//  Terminal-style block with copy-to-clipboard.
//  Props:
//    lines – array of { cmd: string, comment?: string }
// ============================================================
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");
    const handleCopy = () => { navigator.clipboard.writeText(fullText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (<Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12, transition: "color 0.2s", "&:hover": { color: BRAND.gold } }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 12, md: 13 }, color: BRAND.codeText, wordBreak: "break-all" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: WarningBox / SuccessBox
// ============================================================
function WarningBox({ children }) {
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 2, mb: 2, background: BRAND.danger, border: `1px solid ${BRAND.dangerBorder}`, borderRadius: 2 }}>
            <WarningAmberIcon sx={{ color: BRAND.dangerText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.dangerText, fontSize: 13, lineHeight: 1.7 }}>{children}</Typography>
        </Box>
    );
}

function SuccessBox({ children }) {
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 2, mb: 2, background: BRAND.success, border: `1px solid ${BRAND.successBorder}`, borderRadius: 2 }}>
            <CheckCircleIcon sx={{ color: BRAND.successText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.successText, fontSize: 13, lineHeight: 1.7 }}>{children}</Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
//  Accordion for deeper content sections.
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`, borderRadius: 2.5, overflow: "hidden", mb: 1.5, transition: "border-color 0.25s" }}>
            <Box onClick={() => setOpen((p) => !p)} sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", background: open ? BRAND.goldGlow : BRAND.surface, transition: "background 0.25s", "&:hover": { background: BRAND.goldGlow } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>
            <Collapse in={open}>
                <Box sx={{ px: 2.5, py: 2, background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${BRAND.border}` }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: PkgManagerHeader
//  Coloured banner for each package manager section.
//  Props:
//    icon    – MUI icon element
//    name    – package manager name (e.g. "APT")
//    distros – distro list string
//    color   – accent colour
// ============================================================
function PkgManagerHeader({ icon, name, distros, color }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, mb: 3, background: `${color}0d`, border: `1px solid ${color}33`, borderRadius: 2 }}>
            <Box sx={{ width: 46, height: 46, borderRadius: 2, background: `${color}18`, border: `1px solid ${color}45`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
            </Box>
            <Box>
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 800, fontSize: { xs: 16, md: 18 }, color, lineHeight: 1.2 }}>{name}</Typography>
                <Typography sx={{ fontSize: 12, color: BRAND.muted, mt: 0.3 }}>{distros}</Typography>
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: CommandRow
//  A two-column reference row: command + description.
//  Used in the quick-reference tables within each section.
// ============================================================
function CommandRow({ cmd, description, color }) {
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap", gap: { xs: 1, sm: 2 }, py: 1.3, px: 1.5, borderBottom: `1px solid ${BRAND.border}`, "&:last-child": { borderBottom: "none" }, transition: "background 0.2s", "&:hover": { background: "rgba(255,255,255,0.02)" } }}>
            <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 12, md: 13 }, color: color || BRAND.goldBright, fontWeight: 600, minWidth: { xs: "100%", sm: 230 }, flexShrink: 0, wordBreak: "break-all" }}>{cmd}</Typography>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, flex: 1, lineHeight: 1.6 }}>{description}</Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
//  Styled external-link row.
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box component="a" href={href} target="_blank" rel="noreferrer" sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, mb: 1.2, textDecoration: "none", border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.surface, transition: "border-color 0.25s, box-shadow 0.25s", "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 16px ${BRAND.goldGlow}` } }}>
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>{label}</Typography>
                {sub && <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>{sub}</Typography>}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: PkgComparisonTable
//  Side-by-side table: same operation across APT / DNF / Pacman
//  Makes it easy for students to switch between distros.
// ============================================================
function PkgComparisonTable() {
    const rows = [
        { op: "Update package index", apt: "apt update",              dnf: "dnf check-update",         pacman: "pacman -Sy"      },
        { op: "Upgrade all packages", apt: "apt upgrade",             dnf: "dnf upgrade",              pacman: "pacman -Syu"     },
        { op: "Install a package",    apt: "apt install nginx",       dnf: "dnf install httpd",        pacman: "pacman -S nginx" },
        { op: "Remove a package",     apt: "apt remove nginx",        dnf: "dnf remove httpd",         pacman: "pacman -R nginx" },
        { op: "Remove + config",      apt: "apt purge nginx",         dnf: "dnf remove --purge httpd", pacman: "pacman -Rns nginx"},
        { op: "Search for a package", apt: "apt search nginx",        dnf: "dnf search httpd",         pacman: "pacman -Ss nginx"},
        { op: "Show package info",    apt: "apt show nginx",          dnf: "dnf info httpd",           pacman: "pacman -Si nginx"},
        { op: "List installed",       apt: "dpkg -l",                 dnf: "rpm -qa",                  pacman: "pacman -Q"       },
        { op: "Which pkg owns file",  apt: "dpkg -S /usr/bin/nginx",  dnf: "rpm -qf /usr/bin/httpd",   pacman: "pacman -Qo file" },
        { op: "List package files",   apt: "dpkg -L nginx",           dnf: "rpm -ql httpd",            pacman: "pacman -Ql nginx"},
        { op: "Remove orphans",       apt: "apt autoremove",          dnf: "dnf autoremove",           pacman: "pacman -Rns $(pacman -Qdtq)"},
        { op: "Clean cache",          apt: "apt clean",               dnf: "dnf clean all",            pacman: "pacman -Sc"      },
        { op: "Add repository",       apt: "add-apt-repository ...",  dnf: "dnf config-manager ...",   pacman: "(edit /etc/pacman.conf)"},
    ];

    return (
        <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, overflowX: "auto", mb: 3 }}>
            {/* Header */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1.4fr 1fr 1fr 1fr" }, gap: 0, borderBottom: `1px solid ${BRAND.border}`, background: BRAND.goldGlow, minWidth: 560 }}>
                {["Operation", "APT (Debian/Ubuntu)", "DNF (Fedora/RHEL)", "Pacman (Arch)"].map((h, i) => (
                    <Box key={h} sx={{ px: 1.5, py: 1, borderRight: i < 3 ? `1px solid ${BRAND.border}` : "none" }}>
                        <Typography sx={{ fontSize: 11, color: [BRAND.gold, BRAND.apt, BRAND.dnf, BRAND.pacman][i], fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase" }}>{h}</Typography>
                    </Box>
                ))}
            </Box>

            {/* Rows */}
            {rows.map(({ op, apt, dnf, pacman }, ri) => (
                <Box key={op} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1.4fr 1fr 1fr 1fr" }, gap: 0, borderBottom: ri < rows.length - 1 ? `1px solid ${BRAND.border}` : "none", minWidth: 560, "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}>
                    {/* Operation label */}
                    <Box sx={{ px: 1.5, py: 1.2, borderRight: `1px solid ${BRAND.border}` }}>
                        <Typography sx={{ fontSize: 12, color: BRAND.muted }}>{op}</Typography>
                    </Box>
                    {/* APT command */}
                    <Box sx={{ px: 1.5, py: 1.2, borderRight: `1px solid ${BRAND.border}` }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: BRAND.apt, wordBreak: "break-all" }}>{apt}</Typography>
                    </Box>
                    {/* DNF command */}
                    <Box sx={{ px: 1.5, py: 1.2, borderRight: `1px solid ${BRAND.border}` }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: BRAND.dnf, wordBreak: "break-all" }}>{dnf}</Typography>
                    </Box>
                    {/* Pacman command */}
                    <Box sx={{ px: 1.5, py: 1.2 }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: BRAND.pacman, wordBreak: "break-all" }}>{pacman}</Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: LinuxPackageManagement
//  Full "Linux Package Management" lesson page.
// ============================================================
export default function LinuxPackageManagement() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 } }}>
            <Container maxWidth="lg">

                {/* ══════════════════════════════════════════════
            HERO HEADER
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Systems · Module 11"
                        sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}
                    />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 26, sm: 34, md: 42 }, mb: 2, "& span": { background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" } }}>
                        Linux <span>Package Management</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 680, lineHeight: 1.8, mx: { xs: "auto", md: 0 }, mb: 3 }}>
                        Package managers are the App Stores of Linux — but far more powerful. They resolve
                        dependencies, verify package signatures, manage configuration files, and handle
                        every aspect of software installation from a single command. Understanding your
                        distro's package manager — and knowing when to reach for universal formats — is
                        one of the most practical skills in Linux administration.
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        {[{ label: "~2.5 hours", sub: "read time" }, { label: "Beginner", sub: "level" }, { label: "5 managers", sub: "covered" }].map(({ label, sub }) => (
                            <Box key={label} sx={{ textAlign: "center", background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, px: 2.5, py: 1.2 }}>
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 15 }}>{label}</Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – KEY CONCEPTS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LayersIcon />} label="Key Concepts" />
                    <Grid container spacing={2}>
                        {[
                            { icon: <InventoryIcon />, title: "Packages", description: "A package is a compressed archive containing a program's binary files, config files, man pages, and metadata (name, version, dependencies). Package managers track every file a package installed so removal is clean." },
                            { icon: <AccountTreeIcon />, title: "Dependencies", description: "Most software depends on shared libraries and other packages. Package managers automatically calculate, download, and install every dependency. Without a manager, you'd do this manually — known as 'dependency hell'." },
                            { icon: <StorageIcon />, title: "Repositories", description: "A repository (repo) is a curated server hosting thousands of packages. Your package manager fetches packages from repos. Official repos are maintained by the distro team and verified with GPG signatures." },
                            { icon: <LockIcon />, title: "GPG Verification", description: "Every package is signed with the repo's GPG key. Your package manager verifies the signature before installing. This ensures you are installing untampered software from the legitimate publisher." },
                            { icon: <AutorenewIcon />, title: "Update vs Upgrade", description: "'Update' refreshes the local index of available packages from repos. 'Upgrade' downloads and installs newer versions of installed packages. Always run update before upgrade." },
                            { icon: <CloudDownloadIcon />, title: "Package Cache", description: "Downloaded packages are stored in a local cache (/var/cache/apt/ or /var/cache/pacman/). This allows reinstallation without re-downloading. The cache can be cleaned to free disk space." },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.title}><ConceptCard {...card} /></Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – APT (DEBIAN / UBUNTU)
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<BuildIcon />} label="APT — Debian, Ubuntu & Derivatives" />

                    <PkgManagerHeader
                        icon={<BuildIcon />}
                        name="APT — Advanced Package Tool"
                        distros="Debian · Ubuntu · Linux Mint · Pop!_OS · Kali Linux · Raspberry Pi OS"
                        color={BRAND.apt}
                    />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        APT is the package manager for all Debian-based distributions. It works on top of{" "}
                        <code style={{ color: BRAND.goldBright }}>dpkg</code> (the low-level package
                        installer) and adds dependency resolution, repo management, and automatic updates.
                        Always run <code style={{ color: BRAND.goldBright }}>sudo apt update</code> before
                        installing anything to ensure you get the latest available version.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>🟡 Core APT Commands</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ sudo apt update",                                comment: "Refresh the local package index from all repos" },
                        { cmd: "$ sudo apt upgrade",                               comment: "Upgrade all installed packages to their latest versions" },
                        { cmd: "$ sudo apt full-upgrade",                          comment: "Like upgrade but also removes conflicting packages if needed" },
                        { cmd: "$ sudo apt install nginx",                         comment: "Install a package (and all its dependencies automatically)" },
                        { cmd: "$ sudo apt install nginx curl wget -y",            comment: "Install multiple packages at once (-y = yes to all prompts)" },
                        { cmd: "$ sudo apt remove nginx",                          comment: "Remove a package but keep its config files" },
                        { cmd: "$ sudo apt purge nginx",                           comment: "Remove a package AND all its configuration files" },
                        { cmd: "$ sudo apt autoremove",                            comment: "Remove orphaned packages no longer needed as dependencies" },
                        { cmd: "$ sudo apt clean",                                 comment: "Delete all downloaded .deb files from the local cache" },
                        { cmd: "$ sudo apt autoclean",                             comment: "Delete only outdated cached .deb files" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>🟡 Searching & Inspecting</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ apt search nginx",                               comment: "Search all repos for packages matching 'nginx'" },
                        { cmd: "$ apt show nginx",                                 comment: "Show package metadata: version, deps, description, size" },
                        { cmd: "$ apt list --installed",                           comment: "List all installed packages" },
                        { cmd: "$ apt list --upgradable",                          comment: "List packages that have a newer version available" },
                        { cmd: "$ apt depends nginx",                              comment: "Show all dependencies of a package" },
                        { cmd: "$ apt-cache policy nginx",                         comment: "Show installed vs available versions and repo priorities" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>🟡 dpkg — Low-Level Package Tool</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ dpkg -l",                                        comment: "List all installed packages (ii = installed, rc = removed)" },
                        { cmd: "$ dpkg -l | grep nginx",                           comment: "Check if a specific package is installed" },
                        { cmd: "$ dpkg -L nginx",                                  comment: "List all files installed by a package" },
                        { cmd: "$ dpkg -S /usr/bin/nginx",                         comment: "Find which package owns a specific file" },
                        { cmd: "$ sudo dpkg -i package.deb",                       comment: "Install a .deb file manually (from outside the repos)" },
                        { cmd: "$ dpkg --get-selections",                          comment: "Export all installed package names (for backup/restore)" },
                    ]} />

                    <ExpandableExample title="Adding external repositories & PPAs (Ubuntu)" icon={<StorageIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            When a package isn't in the official Ubuntu repos, you can add a third-party
                            repository. Always verify the source and GPG key before adding external repos.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ sudo add-apt-repository ppa:deadsnakes/ppa",    comment: "Add a PPA (Personal Package Archive) for Ubuntu" },
                            { cmd: "$ sudo apt update",                               comment: "Refresh index after adding a new repo" },
                            { cmd: "$ sudo apt install python3.12",                   comment: "Install from the newly added PPA" },
                            { cmd: "$ cat /etc/apt/sources.list",                     comment: "View official repo sources" },
                            { cmd: "$ ls /etc/apt/sources.list.d/",                  comment: "View added third-party repos (each gets its own file)" },
                            { cmd: "$ sudo add-apt-repository --remove ppa:deadsnakes/ppa", comment: "Remove a PPA" },
                        ]} />
                        <WarningBox>
                            Only add PPAs and repos from trusted publishers. A malicious repo can serve
                            modified packages that replace system tools with backdoored versions — giving
                            attackers root access. Verify the PPA owner and GPG key before adding.
                        </WarningBox>
                    </ExpandableExample>

                    <ExpandableExample title="Holding packages back from upgrades" icon={<LockIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Sometimes you need to pin a package at a specific version — for example, locking
                            a kernel version or a database version that works with your application.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ sudo apt-mark hold nginx",                      comment: "Hold nginx at current version — it will never be upgraded" },
                            { cmd: "$ sudo apt-mark unhold nginx",                    comment: "Release the hold" },
                            { cmd: "$ apt-mark showhold",                             comment: "List all packages currently on hold" },
                            { cmd: "$ sudo dpkg --set-selections <<< 'nginx hold'",   comment: "Alternative method using dpkg directly" },
                        ]} />
                    </ExpandableExample>

                    <ExpandableExample title="Unattended upgrades — automatic security patches" icon={<SecurityIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            For production servers, install{" "}
                            <code style={{ color: BRAND.goldBright }}>unattended-upgrades</code> to
                            automatically apply security patches without manual intervention.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ sudo apt install unattended-upgrades",          comment: "Install the unattended upgrades package" },
                            { cmd: "$ sudo dpkg-reconfigure -plow unattended-upgrades", comment: "Enable and configure via interactive prompts" },
                            { cmd: "$ cat /etc/apt/apt.conf.d/50unattended-upgrades", comment: "Review what will be auto-updated" },
                            { cmd: "$ sudo unattended-upgrade --dry-run",             comment: "Simulate what would be upgraded (safe test)" },
                            { cmd: "$ cat /var/log/unattended-upgrades/unattended-upgrades.log", comment: "Check what was auto-upgraded and when" },
                        ]} />
                        <SuccessBox>
                            Security-only auto-upgrades are a best practice on production servers. They patch
                            known CVEs automatically without risking application breakage from major version changes.
                        </SuccessBox>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – DNF / YUM (FEDORA / RHEL)
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SettingsIcon />} label="DNF & YUM — Fedora, RHEL & CentOS" />

                    <PkgManagerHeader
                        icon={<SettingsIcon />}
                        name="DNF — Dandified YUM"
                        distros="Fedora · RHEL · CentOS Stream · Rocky Linux · AlmaLinux · Oracle Linux"
                        color={BRAND.dnf}
                    />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        DNF replaced YUM as the default package manager on Fedora 22+ and RHEL 8+.
                        It is faster, has better dependency resolution, and supports a plugin system.{" "}
                        <code style={{ color: BRAND.goldBright }}>yum</code> is still an alias on older
                        RHEL/CentOS systems. Packages use the <strong style={{ color: BRAND.white }}>.rpm</strong>{" "}
                        format and the underlying tool is <code style={{ color: BRAND.goldBright }}>rpm</code>.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>🟡 Core DNF Commands</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ sudo dnf check-update",                          comment: "Check for available updates (doesn't install)" },
                        { cmd: "$ sudo dnf upgrade",                               comment: "Upgrade all installed packages" },
                        { cmd: "$ sudo dnf install httpd",                         comment: "Install Apache web server" },
                        { cmd: "$ sudo dnf install httpd mariadb-server -y",       comment: "Install multiple packages with auto-yes" },
                        { cmd: "$ sudo dnf remove httpd",                          comment: "Remove a package" },
                        { cmd: "$ sudo dnf autoremove",                            comment: "Remove packages no longer needed as dependencies" },
                        { cmd: "$ sudo dnf clean all",                             comment: "Clear all cached package data" },
                        { cmd: "$ sudo dnf group install 'Development Tools'",     comment: "Install a package group (gcc, make, etc.)" },
                        { cmd: "$ sudo dnf group list",                            comment: "List all available package groups" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>🟡 Searching, Info & RPM</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ dnf search httpd",                               comment: "Search repos for packages matching 'httpd'" },
                        { cmd: "$ dnf info httpd",                                 comment: "Show package info: version, repo, description" },
                        { cmd: "$ dnf list installed",                             comment: "List all installed packages" },
                        { cmd: "$ dnf provides /usr/bin/python3",                  comment: "Find which package provides a specific file or command" },
                        { cmd: "$ rpm -qa",                                        comment: "List all installed RPM packages" },
                        { cmd: "$ rpm -qi httpd",                                  comment: "Show detailed info about an installed package" },
                        { cmd: "$ rpm -ql httpd",                                  comment: "List all files installed by a package" },
                        { cmd: "$ rpm -qf /etc/httpd/conf/httpd.conf",             comment: "Find which package owns a file" },
                        { cmd: "$ sudo rpm -i package.rpm",                        comment: "Install an RPM file directly (no dependency resolution)" },
                        { cmd: "$ sudo rpm -Uvh package.rpm",                      comment: "-U = upgrade, -v = verbose, -h = progress hash marks" },
                    ]} />

                    <ExpandableExample title="Managing repos with DNF and .repo files" icon={<StorageIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Third-party repos on Fedora/RHEL are configured in{" "}
                            <code style={{ color: BRAND.goldBright }}>/etc/yum.repos.d/*.repo</code> files.
                            EPEL (Extra Packages for Enterprise Linux) is the most important third-party repo
                            for RHEL-based systems.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ dnf repolist",                                  comment: "List all enabled repositories" },
                            { cmd: "$ dnf repolist all",                              comment: "List all repos including disabled ones" },
                            { cmd: "$ sudo dnf install epel-release",                 comment: "Add the EPEL repository (thousands of extra packages)" },
                            { cmd: "$ sudo dnf config-manager --enable powertools",   comment: "Enable the PowerTools/CRB repo (needed by many EPEL packages)" },
                            { cmd: "$ ls /etc/yum.repos.d/",                         comment: "View all configured repo files" },
                            { cmd: "$ sudo dnf config-manager --add-repo URL",        comment: "Add a repo from a URL" },
                        ]} />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – PACMAN (ARCH LINUX)
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SpeedIcon />} label="Pacman — Arch Linux" />

                    <PkgManagerHeader
                        icon={<SpeedIcon />}
                        name="Pacman"
                        distros="Arch Linux · Manjaro · EndeavourOS · Garuda Linux · ArcoLinux"
                        color={BRAND.pacman}
                    />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Pacman is one of the fastest and simplest package managers. Arch uses a rolling
                        release model — there are no version upgrades, just continuous updates.
                        <code style={{ color: BRAND.goldBright }}>-S</code> means Sync (from repos),{" "}
                        <code style={{ color: BRAND.goldBright }}>-Q</code> means Query (local database),{" "}
                        <code style={{ color: BRAND.goldBright }}>-R</code> means Remove.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>🟡 Core Pacman Commands</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ sudo pacman -Syu",                               comment: "Sync repos + upgrade all packages (always do this first)" },
                        { cmd: "$ sudo pacman -S firefox",                         comment: "Install a package" },
                        { cmd: "$ sudo pacman -S firefox chromium vlc",            comment: "Install multiple packages at once" },
                        { cmd: "$ sudo pacman -R firefox",                         comment: "Remove a package (keep deps and configs)" },
                        { cmd: "$ sudo pacman -Rs firefox",                        comment: "-s = also remove unneeded dependencies" },
                        { cmd: "$ sudo pacman -Rns firefox",                       comment: "-n = also remove config files (clean purge)" },
                        { cmd: "$ sudo pacman -Sc",                                comment: "Clean the package cache (remove old versions)" },
                        { cmd: "$ sudo pacman -Scc",                               comment: "Clean entire cache (removes ALL cached packages)" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>🟡 Querying & Searching</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ pacman -Ss firefox",                             comment: "Search repos for packages matching 'firefox'" },
                        { cmd: "$ pacman -Si firefox",                             comment: "Show info about a package in the repos" },
                        { cmd: "$ pacman -Q",                                      comment: "List all installed packages" },
                        { cmd: "$ pacman -Qs firefox",                             comment: "Search installed packages" },
                        { cmd: "$ pacman -Qi firefox",                             comment: "Show info about an installed package" },
                        { cmd: "$ pacman -Ql firefox",                             comment: "List all files installed by a package" },
                        { cmd: "$ pacman -Qo /usr/bin/firefox",                    comment: "Find which package owns a file" },
                        { cmd: "$ pacman -Qdt",                                    comment: "List orphaned packages (dependencies with no dependents)" },
                        { cmd: "$ sudo pacman -Rns $(pacman -Qdtq)",               comment: "Remove all orphans in one command" },
                    ]} />

                    <ExpandableExample title="AUR — Arch User Repository" icon={<PublicIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            The AUR is a community repository containing thousands of packages not in the
                            official repos. You don't use pacman directly — you use an{" "}
                            <strong style={{ color: BRAND.white }}>AUR helper</strong> like{" "}
                            <code style={{ color: BRAND.goldBright }}>yay</code> or{" "}
                            <code style={{ color: BRAND.goldBright }}>paru</code>. These build packages
                            from source (PKGBUILDs) and install them via pacman.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ sudo pacman -S --needed git base-devel",       comment: "Install build tools needed by AUR helpers" },
                            { cmd: "$ git clone https://aur.archlinux.org/yay.git && cd yay && makepkg -si", comment: "Build and install yay AUR helper" },
                            { cmd: "$ yay -Syu",                                     comment: "Upgrade system + AUR packages together" },
                            { cmd: "$ yay -S google-chrome",                         comment: "Install a package from the AUR" },
                            { cmd: "$ yay -Ss chrome",                               comment: "Search both repos and AUR" },
                        ]} />
                        <WarningBox>
                            AUR packages are user-submitted and not vetted by Arch maintainers.
                            Always read the PKGBUILD before installing an AUR package to verify it
                            doesn't run malicious code. Never run <code>yay</code> as root.
                        </WarningBox>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – SIDE-BY-SIDE COMPARISON TABLE
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AccountTreeIcon />} label="Package Manager Comparison" />
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        The same operation across all three major native package managers — colour-coded
                        so you can quickly find the equivalent command when switching distros.
                    </Typography>
                    <PkgComparisonTable />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 6 – UNIVERSAL PACKAGE MANAGERS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PublicIcon />} label="Universal Package Formats" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Universal package formats are distribution-agnostic — they bundle the app and all
                        its dependencies into a single portable unit that runs on any Linux system.
                        Each has trade-offs in size, sandboxing, and integration with the host system.
                    </Typography>

                    {/* Snap */}
                    <PkgManagerHeader icon={<ExtensionIcon />} name="Snap" distros="Ubuntu default · Canonical-maintained · Any Linux with snapd installed" color={BRAND.snap} />
                    <CodeBlock lines={[
                        { cmd: "$ sudo snap install vlc",                           comment: "Install a snap package" },
                        { cmd: "$ sudo snap install code --classic",                comment: "--classic = relaxed sandboxing (needed for some apps like VS Code)" },
                        { cmd: "$ snap list",                                       comment: "List installed snap packages" },
                        { cmd: "$ snap find 'video player'",                        comment: "Search the Snap Store" },
                        { cmd: "$ sudo snap refresh vlc",                           comment: "Update a specific snap" },
                        { cmd: "$ sudo snap refresh",                               comment: "Update all snaps" },
                        { cmd: "$ sudo snap remove vlc",                            comment: "Remove a snap package" },
                        { cmd: "$ snap info vlc",                                   comment: "Show snap details, versions, and channels" },
                        { cmd: "$ snap connections vlc",                            comment: "Show permissions/interfaces the snap uses" },
                    ]} />

                    {/* Flatpak */}
                    <PkgManagerHeader icon={<CloudDownloadIcon />} name="Flatpak" distros="Fedora default · Available on all distros · Flathub is the main repo" color={BRAND.flatpak} />
                    <CodeBlock lines={[
                        { cmd: "$ flatpak remote-add --if-not-exists flathub https://dl.flathub.org/repo/flathub.flatpakrepo", comment: "Add Flathub repo (if not already added)" },
                        { cmd: "$ flatpak install flathub org.gimp.GIMP",           comment: "Install GIMP from Flathub" },
                        { cmd: "$ flatpak install flathub com.spotify.Client",      comment: "Install Spotify" },
                        { cmd: "$ flatpak list",                                    comment: "List all installed Flatpaks" },
                        { cmd: "$ flatpak update",                                  comment: "Update all Flatpak packages" },
                        { cmd: "$ flatpak uninstall org.gimp.GIMP",                 comment: "Remove a Flatpak" },
                        { cmd: "$ flatpak run org.gimp.GIMP",                       comment: "Launch a Flatpak from the terminal" },
                        { cmd: "$ flatpak search gimp",                             comment: "Search Flathub for packages" },
                    ]} />

                    {/* AppImage */}
                    <PkgManagerHeader icon={<InventoryIcon />} name="AppImage" distros="No installation required · Runs on any Linux distro · Single portable file" color="#f59e0b" />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7, mb: 2 }}>
                        AppImages are single executable files — download, mark executable, run. No package
                        manager, no root required. Perfect for distributing apps that need to run on many
                        different distros without compatibility issues.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "$ wget https://example.com/app.AppImage",           comment: "Download the AppImage file" },
                        { cmd: "$ chmod +x app.AppImage",                           comment: "Make it executable (required before first run)" },
                        { cmd: "$ ./app.AppImage",                                  comment: "Run the app — no installation needed" },
                        { cmd: "$ ./app.AppImage --appimage-extract",               comment: "Extract the contents to a squashfs-root/ folder" },
                        { cmd: "$ sudo apt install appimaged",                      comment: "Optional: integrate AppImages into the desktop (system tray, launchers)" },
                    ]} />

                    {/* Comparison table */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5, mt: 2 }}>🟡 Snap vs Flatpak vs AppImage</Typography>
                    <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, overflowX: "auto", mb: 2 }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr", minWidth: 500, borderBottom: `1px solid ${BRAND.border}`, background: BRAND.goldGlow }}>
                            {["Feature", "Snap", "Flatpak", "AppImage"].map((h, i) => (
                                <Box key={h} sx={{ px: 1.5, py: 1, borderRight: i < 3 ? `1px solid ${BRAND.border}` : "none" }}>
                                    <Typography sx={{ fontSize: 11, color: [BRAND.gold, BRAND.snap, BRAND.flatpak, "#f59e0b"][i], fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase" }}>{h}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {[
                            { feature: "Sandboxed",      snap: "✓ Yes",      flatpak: "✓ Yes",        appimage: "✗ No"        },
                            { feature: "Root required",  snap: "Install: yes", flatpak: "Install: yes", appimage: "✗ Never"    },
                            { feature: "Auto-updates",   snap: "✓ Automatic", flatpak: "Manual/auto",  appimage: "App decides" },
                            { feature: "Offline use",    snap: "Limited",     flatpak: "✓ Yes",        appimage: "✓ Yes"       },
                            { feature: "Size",           snap: "Large",       flatpak: "Large",         appimage: "Varies"      },
                            { feature: "Host integration", snap: "Good",     flatpak: "Better",        appimage: "Basic"       },
                            { feature: "Main store",     snap: "Snap Store",  flatpak: "Flathub",       appimage: "No central store" },
                        ].map(({ feature, snap, flatpak, appimage }, ri) => (
                            <Box key={feature} sx={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr", minWidth: 500, borderBottom: ri < 6 ? `1px solid ${BRAND.border}` : "none", "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}>
                                <Box sx={{ px: 1.5, py: 1.1, borderRight: `1px solid ${BRAND.border}` }}><Typography sx={{ fontSize: 12, color: BRAND.muted }}>{feature}</Typography></Box>
                                <Box sx={{ px: 1.5, py: 1.1, borderRight: `1px solid ${BRAND.border}` }}><Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.snap }}>{snap}</Typography></Box>
                                <Box sx={{ px: 1.5, py: 1.1, borderRight: `1px solid ${BRAND.border}` }}><Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.flatpak }}>{flatpak}</Typography></Box>
                                <Box sx={{ px: 1.5, py: 1.1 }}><Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "#f59e0b" }}>{appimage}</Typography></Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 7 – LANGUAGE PACKAGE MANAGERS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="Language Package Managers" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Beyond system package managers, every major programming language has its own
                        package ecosystem. These install language-specific libraries and tools, not system
                        packages. You will use these daily as a developer.
                    </Typography>

                    <ExpandableExample title="pip — Python Package Manager" icon={<TerminalIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            <code style={{ color: BRAND.goldBright }}>pip</code> installs Python packages
                            from PyPI. Always use virtual environments (<code style={{ color: BRAND.goldBright }}>venv</code>)
                            to avoid polluting the system Python installation.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ pip install requests",                          comment: "Install a Python package" },
                            { cmd: "$ pip install requests==2.28.0",                  comment: "Install a specific version" },
                            { cmd: "$ pip install -r requirements.txt",               comment: "Install all packages listed in a requirements file" },
                            { cmd: "$ pip list",                                      comment: "List installed packages" },
                            { cmd: "$ pip show requests",                             comment: "Show details about an installed package" },
                            { cmd: "$ pip uninstall requests",                        comment: "Uninstall a package" },
                            { cmd: "$ pip freeze > requirements.txt",                 comment: "Export all installed packages to a requirements file" },
                            { cmd: "$ python3 -m venv .venv",                         comment: "Create a virtual environment in .venv/" },
                            { cmd: "$ source .venv/bin/activate",                     comment: "Activate the virtual environment" },
                            { cmd: "$ deactivate",                                    comment: "Exit the virtual environment" },
                        ]} />
                    </ExpandableExample>

                    <ExpandableExample title="npm — Node.js Package Manager" icon={<TerminalIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            <code style={{ color: BRAND.goldBright }}>npm</code> installs JavaScript packages
                            from the npm registry. Local installs go into{" "}
                            <code style={{ color: BRAND.goldBright }}>node_modules/</code>. Global installs
                            (with <code style={{ color: BRAND.goldBright }}>-g</code>) provide CLI tools.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ npm install express",                           comment: "Install a package locally (saved to package.json)" },
                            { cmd: "$ npm install -g typescript",                     comment: "Install a package globally (available as CLI tool)" },
                            { cmd: "$ npm install",                                   comment: "Install all packages from package.json" },
                            { cmd: "$ npm uninstall express",                         comment: "Uninstall a package" },
                            { cmd: "$ npm list",                                      comment: "List locally installed packages" },
                            { cmd: "$ npm outdated",                                  comment: "Check which packages have newer versions" },
                            { cmd: "$ npm update",                                    comment: "Update all packages within semver ranges" },
                            { cmd: "$ npm audit",                                     comment: "Check for known security vulnerabilities" },
                            { cmd: "$ npm audit fix",                                 comment: "Automatically fix vulnerable packages where possible" },
                        ]} />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 8 – WHAT YOU HAVE LEARNED
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />
                    <Grid container spacing={1.5}>
                        {[
                            "What packages, dependencies, repositories, and GPG verification are",
                            "The difference between apt update and apt upgrade — and why order matters",
                            "Core APT commands: install, remove, purge, autoremove, clean",
                            "dpkg commands: list, file ownership, manual .deb installation",
                            "How to add third-party PPAs and repos safely on Ubuntu",
                            "How to hold packages at a fixed version with apt-mark hold",
                            "How to enable automatic security patches with unattended-upgrades",
                            "Core DNF commands and the equivalent rpm low-level operations",
                            "How to add the EPEL repo and manage .repo files on RHEL systems",
                            "Pacman's S/Q/R flag system and rolling-release update workflow",
                            "How the AUR works and how to install packages with yay",
                            "The difference between Snap, Flatpak, and AppImage universal formats",
                            "How to run AppImages without root or installation",
                            "pip virtual environments and requirements.txt workflow for Python",
                            "npm local vs global installs and npm audit for vulnerability scanning",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 9 – RESOURCES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />
                    <ResourceLink href="https://wiki.debian.org/Apt" label="Debian APT Wiki" sub="Official Debian documentation covering apt, apt-get, apt-cache, and dpkg." />
                    <ResourceLink href="https://wiki.archlinux.org/title/Pacman" label="Arch Wiki — Pacman" sub="Complete Pacman reference including tips, AUR integration, and troubleshooting." />
                    <ResourceLink href="https://snapcraft.io/docs" label="Snapcraft Documentation" sub="Official Snap documentation covering installation, channels, and confinement." />
                    <ResourceLink href="https://docs.flatpak.org/" label="Flatpak Documentation" sub="Official Flatpak guide including Flathub setup, building, and sandboxing." />
                    <ResourceLink href="https://dnf.readthedocs.io/" label="DNF Documentation" sub="Full DNF reference: command syntax, config files, plugin system." />
                    <ResourceLink href="https://pip.pypa.io/en/stable/" label="pip Documentation" sub="Official pip docs: installation, virtual environments, requirements files." />
                </Box>

                {/* ── Footer pro tip ── */}
                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0, mt: 0.1 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> Always run{" "}
                        <code style={{ color: BRAND.goldBright }}>sudo apt update</code> before{" "}
                        <code style={{ color: BRAND.goldBright }}>sudo apt install</code> — otherwise you
                        may install an outdated version or fail due to stale dependency metadata. On Arch,
                        always do a full <code style={{ color: BRAND.goldBright }}>pacman -Syu</code> before
                        installing anything — never do a partial upgrade (<code style={{ color: BRAND.goldBright }}>pacman -Sy package</code>)
                        as it can break the system.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
